/**
 * Normalize P&L outputs into atomic, non-overlapping Stage Finance values.
 *
 * Stage Finance times and finances these amounts; it must not invent residuals
 * or silently modify P&L inputs.
 *
 * Identity (when complete):
 *   salesRevenue − sum(pnlCostAtoms) = netProfit
 */

import { n, nearlyEqual } from './math.js';

function softFromComponents(outputs) {
  const arch = n(outputs.architectureCost);
  const consult = n(outputs.consultantsCost);
  const contig = n(outputs.contingencyCost);
  const sum = arch + consult + contig;
  const soft = n(outputs.softCosts);
  if (soft > 0 && sum > 0 && !nearlyEqual(soft, sum, 1)) {
    return { softCosts: soft, usedComponents: false, conflict: true };
  }
  if (soft > 0) return { softCosts: soft, usedComponents: false, conflict: false };
  return { softCosts: sum, usedComponents: true, conflict: false };
}

/**
 * @param {object} outputs DealOutputs from the calculation engine
 * @returns {object} Normalized P&L band for Stage Finance
 */
export function normalizePnl(outputs = {}) {
  const o = outputs && typeof outputs === 'object' ? outputs : {};
  const incompleteReasons = [];

  const salesRevenue = n(o.totalRevenue) || n(o.salesRevenue) + n(o.parkingRevenue);
  const units = Math.max(0, Math.round(n(o.numberOfUnits)));

  const landPrice = n(o.landCost);
  const totalLandCost = n(o.totalLandCost) || landPrice + n(o.notaryFees) + n(o.otherLandFees);
  const acquisitionCosts = Math.max(0, totalLandCost - landPrice);

  const constructionCost = n(o.constructionCost);
  const constructionReserve = n(o.constructionReserve);
  const softInfo = softFromComponents(o);
  const softCosts = softInfo.softCosts;
  const commercialCosts = n(o.commercializationCost);
  const pilotageCost = n(o.pilotageCost);
  const professionalSoftCosts = softCosts + pilotageCost;

  const taxes = n(o.taxAmount);
  const financingCosts = n(o.totalFinancingCost);
  const feasibilityCosts = n(o.feasibilityStudy);
  const upfrontFees = n(o.upfrontFees);

  const sponsorEquity = n(o.equityRequired);
  const privateFunding = n(o.privateCapitalAmount);
  const securedBankFacilities = n(o.bankDebtAmount);
  const securedConstructionFacility = n(o.constructionFacilityAmount);
  const capitalInvested = n(o.capitalInvested);
  const netProfit = n(o.finalNetProfit) || n(o.profitAvailable);

  const bankBaseSource = o.bankDebtBaseSource || 'landCost';
  const equityBaseSource = o.equityBaseSource || 'landCost';
  const privateBaseSource = o.privateCapitalBaseSource || 'landCost';

  // Atomic P&L cost stack for the profit identity.
  // Fees (upfrontFees) are a Funding & Capital allocation of promoter money — not an expense.
  // Feasibility and financing are genuine costs counted once when paid.
  const pnlCostAtoms = {
    landPrice,
    acquisitionCosts,
    constructionCost,
    constructionReserve,
    softCosts,
    commercialCosts,
    pilotageCost,
    taxes,
    financingCosts,
  };
  const pnlCostTotal = Object.values(pnlCostAtoms).reduce((s, v) => s + v, 0);

  // Cash timing atoms Stage Finance may pay. Fees funding is excluded — it never
  // invents an expense. Feasibility is paid once as a real project cost.
  const cashCostAtoms = {
    landPrice,
    acquisitionCosts,
    professionalSoftCosts,
    constructionCost,
    constructionReserve,
    commercialCosts,
    taxes,
    financingCosts,
    feasibilityCosts,
  };
  const cashCostTotal = Object.values(cashCostAtoms).reduce((s, v) => s + v, 0);

  // Sponsor Fees allocation (Funding & Capital) — contribution only, not a cost.
  const feesFunding = upfrontFees;
  // Feasibility capital is matched when the feasibility cost atom is paid.
  const feasibilityCapital = feasibilityCosts;

  if (salesRevenue <= 0) incompleteReasons.push('P&L has no sales revenue');
  if (totalLandCost <= 0 && constructionCost <= 0) {
    incompleteReasons.push('P&L has no land or construction costs');
  }
  if (softInfo.conflict) {
    incompleteReasons.push('softCosts conflicts with architecture+consultants+contingency');
  }

  const expectedProfit = salesRevenue - pnlCostTotal;
  const profitCheckOk = nearlyEqual(expectedProfit, netProfit, 100);
  // P&L may publish developmentCostForProfit separately from atomic cash-cost components.
  // Never schedule cash atoms that disagree with the profit bridge: either the atoms must
  // reconcile to net profit, or Stage Finance is incomplete (do not silently prefer a
  // published total while consuming different atoms).
  const developmentCostForProfit = n(o.developmentCostForProfit);
  if (salesRevenue > 0 && !profitCheckOk) {
    if (developmentCostForProfit > 0) {
      const bridgeProfit = salesRevenue - developmentCostForProfit - taxes - financingCosts;
      if (!nearlyEqual(bridgeProfit, netProfit, 100)) {
        incompleteReasons.push(
          'P&L profit does not reconcile to sales − costs (cannot build a valid cost schedule)',
        );
      } else {
        const atomDevelopmentCost = pnlCostTotal - taxes - financingCosts;
        if (!nearlyEqual(atomDevelopmentCost, developmentCostForProfit, 1)) {
          incompleteReasons.push(
            'Cost components do not match P&L development cost for profit (cannot schedule mismatched cash atoms)',
          );
        }
      }
    } else {
      incompleteReasons.push(
        'P&L profit does not reconcile to sales − costs (cannot build a valid cost schedule)',
      );
    }
  }

  // Capital invested identity: equity + fees + feasibility + financing.
  const capitalBridge = sponsorEquity + upfrontFees + feasibilityCosts + financingCosts;
  const capitalCheckOk = capitalInvested <= 0 || nearlyEqual(capitalBridge, capitalInvested, 1);

  const bankEligibleForLand = ['landCost', 'totalDevCost'].includes(bankBaseSource);
  const bankEligibleForConstruction = ['constructionCost', 'totalDevCost'].includes(bankBaseSource);

  // Optional price-list unit sales (when the P&L/scenario provides them).
  const priceListUnits = Array.isArray(o.priceListUnits)
    ? o.priceListUnits
    : (Array.isArray(o.unitSales) ? o.unitSales : []);

  const complete = incompleteReasons.length === 0;

  return {
    complete,
    incompleteReasons,
    salesRevenue,
    units,
    priceListUnits,
    landPrice,
    acquisitionCosts,
    totalLandCost,
    feasibilityCosts,
    professionalSoftCosts,
    softCosts,
    pilotageCost,
    constructionCost,
    constructionReserve,
    commercialCosts,
    taxes,
    financingCosts,
    upfrontFees,
    feesFunding,
    feasibilityCapital,
    sponsorEquity,
    privateFunding,
    securedBankFacilities,
    securedConstructionFacility,
    capitalInvested: capitalInvested || capitalBridge,
    netProfit,
    pnlCostAtoms,
    pnlCostTotal,
    cashCostAtoms,
    cashCostTotal,
    /** @deprecated feasibility-only match pool; Fees funding is separate */
    capitalFundingOnly: feasibilityCapital,
    profitCheckOk,
    capitalCheckOk,
    capitalBridge,
    bankBaseSource,
    equityBaseSource,
    privateBaseSource,
    bankEligibleForLand,
    bankEligibleForConstruction,
    bankInterestRate: n(o.bankInterestRate),
    bankDurationMonths: n(o.bankDurationMonths),
    bankSetupCost: n(o.bankSetupCost),
    bankInterestCost: n(o.bankInterestCost),
    developmentCostForProfit: n(o.developmentCostForProfit),
    privateCapitalType: o.privateCapitalType || 'debt',
  };
}
